import { DOCUMENT_REQUEST_LOCATION, IDocumentRequest } from "../models/utilities/document-request";
import { BaseSagaRequest, PagedResultRequest } from "../models/utilities/requests";
import { ToastMessage } from "../models/utilities/toast-message";
import { API_METHODS, CONTENT_TYPES } from "../models/utilities/type-models";
import { UserNotification } from "../models/utilities/user-notification";

/**
 * Toast Actions
 */

        export const UTILITY_SHOW_TOAST = 'UTILITY_SHOW_TOAST';
        export const UTILITY_HIDE_TOAST = 'UTILITY_HIDE_TOAST';

        /**
         * Display a toast notification to the user
         * @param t The toast message to display
         */
        export const showToastNotification = (t: ToastMessage) => ({
            type: UTILITY_SHOW_TOAST,
            payload: t
        });

/**
 * Loading Actions
 */

        export const UTILITY_SHOW_LOADING = 'UTILITY_SHOW_LOADING';
        export const UTILITY_HIDE_LOADING = 'UTILITY_HIDE_LOADING';

        /**
         * Show the loading screen
         */
        export const showLoading = () => ({
            type: UTILITY_SHOW_LOADING
        });

        /**
         * Hide the loading screen
         */
        export const hideLoading = () => ({
            type: UTILITY_HIDE_LOADING
        });

/**
 * Downloading Actions
 */

        export const UTILITY_SHOW_DOWNLOADING = 'UTILITY_SHOW_DOWNLOADING';
        export const UTILITY_HIDE_DOWNLOADING = 'UTILITY_HIDE_DOWNLOADING';

        /**
         * Show the downloading screen
         */
        export const showDownloading = () => ({
            type: UTILITY_SHOW_DOWNLOADING
        });

        /**
         * Hide the downloading screen
         */
        export const hideDownloading = () => ({
            type: UTILITY_HIDE_DOWNLOADING
        });

/**
 * General Actions
 */

    export const UTILITY_GET_DOCUMENT = 'UTILITY_GET_DOCUMENT';

    /**
     * Go fetch a document
     * @param docRequest The document to go fetch
     */
    export const getDocumentHelper = (docRequest: IDocumentRequest) => ({
        type: UTILITY_GET_DOCUMENT,
        payload: docRequest
    });

/**
 * The Middleware Fetch Action
 */

        export const FETCH                      = 'FETCH';
        export const FETCH_AUTOCOMPLETE         = 'FETCH_AUTOCOMPLETE';
        export const FETCH_BLOB                 = 'FETCH_BLOB';
        export const UTILITY_GET_PAGED_RESULT   = 'UTILITY_GET_PAGED_RESULT';
        export const UTILITY_API_REQUEST        = 'UTILITY_API_REQUEST';

        /**
         * A special fetch action creator for running through middleware
         * @param url The URL to fetch
         * @param methodType The type of method to perform
         * @param params Any request parameters needed
         */
        export const fetch = (url: string, methodType: API_METHODS, params: any = undefined) => ({
            type: FETCH,
            url,
            methodType,
            params
        });

        /**
         * A special fetch action creator for running through middleware
         * @param url The URL to fetch
         * @param methodType The type of method to perform
         * @param params Any request parameters needed
         */
        export const fetchAutocomplete = (
            url: string, 
            methodType: API_METHODS, 
            params: any = undefined, 
            serviceCenter?: string) => ({
                type: FETCH_AUTOCOMPLETE,
                url,
                methodType,
                params,
                serviceCenter
        });

        /**
         * A special fetch action creator for running blob requests through middleware
         * @param url The URL to fetch
         * @param methodType The type of method to perform
         * @param params Any request parameters needed
         */
        export const fetchBlob = (url: string, location: DOCUMENT_REQUEST_LOCATION, method: string, data?: any, contentType?: CONTENT_TYPES ) => ({
            type: FETCH_BLOB,
            url,
            location,
            methodType: method,
            contentType: contentType,
            data
        });

        export const getPagedApiResult = (props: PagedResultRequest) => ({
            type: UTILITY_GET_PAGED_RESULT,
            payload: props
        });

        export const makeApiCall = (props: BaseSagaRequest) => ({
            type: UTILITY_API_REQUEST,
            payload: props
        });

/**
 * Application Service Center Actions
 */

        export const SERVICECENTER_ALL_GET = 'SERVICECENTER_ALL_GET';

        /**
         * Action creator for trigger the service center saga to run
         */
        export const fetchAppServiceCenters = () => ({
            type: SERVICECENTER_ALL_GET
        });

/**
 * Scroll State Action
 */

        export const UTILITY_REMEMBER_SCROLL_POSITION = 'UTILITY_REMEMBER_SCROLL_POSITION';
        export const UTILITY_CLEAR_SCROLL_POSITION = 'UTILITY_CLEAR_SCROLL_POSITION';

        export const rememberScrollPosition = (x : number, y: number, key: string) => ({
            type: UTILITY_REMEMBER_SCROLL_POSITION,
            payload: {
                x,
                y,
                subkey: key
            }
        });

        export const clearScrollPosition = (key: string) => ({
            type: UTILITY_CLEAR_SCROLL_POSITION,
            payload: {
                x: 0,
                y: 0,
                subkey: key
            }
        });

/**
 * Notification Actions
 */
        
    export const UTILITY_NOTIFICATION_RESET = 'UTILITY_NOTIFICATION_RESET';
    export const UTILITY_NOTIFICATION_INIT = 'UTILITY_NOTIFICATION_INIT';
    export const UTILITY_NOTIFICATION_ACK = 'UTILITY_NOTIFICATION_ACK';
    export const UTILITY_NOTIFICATION_CLEAR = 'UTILITY_NOTIFICATION_CLEAR';
    export const UTILITY_NOTIFICATION_ACTIVATE = 'UTILITY_NOTIFICATION_ACTIVATE';
    export const UTILITY_NOTIFICATION_DEACTIVATE = 'UTILITY_NOTIFICATION_DEACTIVATE';
    export const UTILITY_NOTIFICATION_ADD = 'UTILITY_NOTIFICATION_ADD';

    export const initializeNotifications = () => ({
        type: UTILITY_NOTIFICATION_INIT
    });

    export const refreshNotifications = () => ({
        type: UTILITY_NOTIFICATION_INIT
    });

    export const resetNotifications = (notifications: UserNotification[]) => ({
        type: UTILITY_NOTIFICATION_RESET,
        payload: notifications
    });

    export const acknowledgeNotification = (notificationId : string) => ({
        type: UTILITY_NOTIFICATION_ACK,
        payload: notificationId
    });

    export const clearNotifications = () => ({
        type: UTILITY_NOTIFICATION_CLEAR
    });

    export const activateNotification = (notificationId : number) => ({
        type: UTILITY_NOTIFICATION_ACTIVATE,
        payload: notificationId
    });

    export const deactivateNotification = (notificationId : number) => ({
        type: UTILITY_NOTIFICATION_DEACTIVATE,
        payload: notificationId
    });

    /**
     * Action creator for adding a notification to the store
     * @param alert Alert object to add
     */
    export const addNotification = (notif : UserNotification) => ({
        type: UTILITY_NOTIFICATION_ADD,
        payload: notif
    });

/**
 * SignalR Actions
 */

    export const SIGNALR_START = 'SIGNALR_START';
    export const SIGNALR_STOP = 'SIGNALR_STOP';
    export const SIGNALR_UPDATE = 'SIGNALR_UPDATE ';
    export const SIGNALR_CONNECTED = 'SIGNALR_CONNECTED';   

    /**
     * Action creator for starting the version check
     */
    export const startSignalR = () => ({
        type: SIGNALR_START
    });

    /**
     * Action creator for stopping the version check
     */
    export const stopSignalR = () => ({
        type: SIGNALR_STOP
    });

    export const signalRUpdate = (newNotification: any) => ({
        type: SIGNALR_UPDATE,
        payload: newNotification
    });

    export const signalRConnected = () => ({
      type: SIGNALR_CONNECTED,
      payload: true
  });